import React, { useState } from "react";
import { Box, Typography, Paper, Stack, Divider, alpha, useTheme, Icon, Tooltip, LinearProgress, Grid, Button, Chip, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import dayjs from "dayjs";
import { BillOfMaterial } from "../../BOM/types";
import { ProcessedItemData } from "../../../Kitchen/ProcessedItems/ProcessedItemFormDialog";
import { ModifiedIngredient, ModifiedProcessedItem } from "../types";

// Extend BillOfMaterial type to include missing properties
export interface ExtendedBillOfMaterial extends BillOfMaterial {
  yield_quantity: number;
  yield_unit: string;
  base_cost: number;
  preparation_time?: number;
  allergens?: string[];
  tags?: string[];
  description: string;
}

interface POSSellableItemSummaryProps {
  formData: ProcessedItemData;
  selectedRecipe: ExtendedBillOfMaterial | null;
  modifiedIngredients: ModifiedIngredient[];
  modifiedProcessedItems: ModifiedProcessedItem[];
  multiplier: number;
  activeStep: number;
}

export const POSSellableItemSummary: React.FC<POSSellableItemSummaryProps> = ({
  formData,
  selectedRecipe,
  modifiedIngredients,
  modifiedProcessedItems,
  multiplier,
  activeStep,
}) => {
  const theme = useTheme();
  const [expandedSection, setExpandedSection] = useState<string | false>("processedItems");

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  // Helper function to handle unit pluralization
  const pluralizeUnit = (quantity: number, unit: string) => {
    if (quantity === 1) {
      // Remove trailing 's' if quantity is 1
      return unit.endsWith('s') ? unit.slice(0, -1) : unit;
    }
    // Add 's' if it doesn't already end with 's'
    return unit.endsWith('s') ? unit : `${unit}s`;
  };

  // Calculate total cost from processed items
  const calculateProcessedItemsCost = () => {
    console.log('Modified Processed Items:', modifiedProcessedItems);
    return modifiedProcessedItems.reduce((total, item) => {
      console.log('Processing item:', item.name, 'total cost:', item.total_cost);
      return total + (item.total_cost || 0);
    }, 0);
  };

  // Calculate total cost from raw materials
  const calculateRawMaterialsCost = () => {
    return modifiedIngredients.reduce((total, item) => {
      const batchCost = item.selected_batches.reduce((batchTotal, batch) => {
        return batchTotal + (batch.quantity * (batch.cost_per_unit || 0));
      }, 0);
      return total + batchCost;
    }, 0);
  };

  // Calculate total cost
  const processedItemsCost = calculateProcessedItemsCost();
  const rawMaterialsCost = calculateRawMaterialsCost();
  const baseCost = processedItemsCost + rawMaterialsCost;
  const totalCost = baseCost;

  const getAvailabilityStatus = (item: { remaining_quantity: number; required_quantity: number }) => {
    const percentage = ((item.required_quantity - item.remaining_quantity) / item.required_quantity) * 100;

    if (percentage >= 100) {
      return {
        status: "success",
        color: theme.palette.success.main,
        icon: "check_circle",
        label: "Fully Available",
        percentage,
      };
    } else if (percentage > 0) {
      return {
        status: "warning",
        color: theme.palette.warning.main,
        icon: "warning",
        label: "Partially Available",
        percentage,
      };
    } else {
      return {
        status: "error",
        color: theme.palette.error.main,
        icon: "error",
        label: "Not Available",
        percentage: 0,
      };
    }
  };

  if (!selectedRecipe) {
    return (
      <Box sx={{ width: "40%", overflow: "auto" }}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 3,
            height: "100%",
            bgcolor: alpha(theme.palette.background.paper, 0.7),
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.05)}`,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Icon sx={{ 
                fontSize: 64, 
                color: alpha(theme.palette.primary.main, 0.2), 
                mb: 3 
              }}>
                restaurant_menu
              </Icon>
              <Typography variant="h5" gutterBottom fontWeight="medium">
                No Recipe Selected
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Select a recipe to see the production summary
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: "auto" }}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          bgcolor: "white",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* Item Name Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha("#0891B2", 0.02),
          }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Menu Item Name
              </Typography>
              {formData.name !== selectedRecipe?.name && (
                <Chip
                  label="Customized"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    height: 20,
                    fontSize: '0.75rem',
                    '& .MuiChip-label': { px: 1 },
                    borderColor: alpha("#0891B2", 0.3),
                    color: "#0891B2"
                  }}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5" fontWeight="600" color="text.primary">
                {formData.name || selectedRecipe?.name || "Untitled Item"}
              </Typography>
              <Icon sx={{ color: alpha("#0891B2", 0.5), fontSize: 20 }}>
                restaurant_menu
              </Icon>
            </Stack>
            {formData.name !== selectedRecipe?.name && (
              <Typography variant="caption" color="text.secondary">
                Original Recipe: {selectedRecipe?.name}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Recipe Output Information */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="600" color="#0891B2">
            Recipe Output
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  bgcolor: alpha("#0891B2", 0.02),
                  borderRadius: 2,
                  border: `1px solid ${alpha("#0891B2", 0.1)}`
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Base Output
                </Typography>
                <Typography variant="h6" color="text.primary" fontWeight="600">
                  {selectedRecipe?.yield_quantity} {pluralizeUnit(selectedRecipe?.yield_quantity || 0, selectedRecipe?.yield_unit || 'unit')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  bgcolor: alpha("#0891B2", 0.02),
                  borderRadius: 2,
                  border: `1px solid ${alpha("#0891B2", 0.1)}`
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Multiplier
                </Typography>
                <Typography variant="h6" color="#0891B2" fontWeight="600">
                  {multiplier}x
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Final Output
                  </Typography>
                  <Typography variant="h5" color="success.main" fontWeight="600">
                    {(selectedRecipe?.yield_quantity || 0) * multiplier} {pluralizeUnit((selectedRecipe?.yield_quantity || 0) * multiplier, selectedRecipe?.yield_unit || 'unit')}
                  </Typography>
                </Box>
                <Icon sx={{ color: alpha(theme.palette.success.main, 0.3), fontSize: 40 }}>
                  restaurant
                </Icon>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Production Timeline */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="600" color="#0891B2">
            Production Timeline
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  bgcolor: alpha("#0891B2", 0.02),
                  borderRadius: 2,
                  border: `1px solid ${alpha("#0891B2", 0.1)}`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    Production Date
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon sx={{ color: alpha("#0891B2", 0.5), fontSize: 20 }}>
                      calendar_today
                    </Icon>
                    <Typography variant="subtitle1" fontWeight="600">
                      {dayjs(formData.production_date).format("MMM D, YYYY")}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  bgcolor: alpha("#0891B2", 0.02),
                  borderRadius: 2,
                  border: `1px solid ${alpha("#0891B2", 0.1)}`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    Expiry Date
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon sx={{ color: alpha("#0891B2", 0.5), fontSize: 20 }}>
                      event_busy
                    </Icon>
                    <Typography variant="subtitle1" fontWeight="600">
                      {dayjs(formData.expiry_date).format("MMM D, YYYY")}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Components Section - Moved here */}
        {/* <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="600" color="#0891B2">
            Components Details
          </Typography> */}
          
          {/* Enhanced Processed Items with Accordion */}
          {/* <Box sx={{ mb: 4 }}>
            <Accordion
              expanded={expandedSection === "processedItems"}
              onChange={handleAccordionChange("processedItems")}
              elevation={0}
              sx={{
                bgcolor: 'transparent',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
                sx={{
                  px: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium" color="primary">
                      Processed Items
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {modifiedProcessedItems.length} {modifiedProcessedItems.length === 1 ? 'item' : 'items'} • {modifiedProcessedItems.reduce((total, item) => total + item.required_quantity, 0)} total units
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" fontWeight="medium">
                    {calculateProcessedItemsCost().toFixed(3)} BHD
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, mt: 2 }}>
                {modifiedProcessedItems.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      textAlign: 'center'
                    }}
                  >
                    <Icon 
                      sx={{ 
                        fontSize: 40, 
                        color: alpha(theme.palette.text.secondary, 0.2),
                        mb: 1
                      }}
                    >
                      category
                    </Icon>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No Processed Items Added
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add processed items to include in this recipe
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {modifiedProcessedItems.map((item) => {
                      const availabilityPercentage = (item.remaining_quantity / item.required_quantity) * 100;
                      const statusColor = availabilityPercentage >= 100 
                        ? theme.palette.success.main 
                        : availabilityPercentage >= 50 
                          ? theme.palette.warning.main 
                          : theme.palette.error.main;
                      
                      return (
                        <Paper
                          key={item.processed_item_id}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                            borderRadius: 2,
                            border: `1px solid ${alpha(statusColor, 0.2)}`,
                          }}
                        >
                          <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="subtitle2" fontWeight="medium">
                                  {item.name}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ mt: 0.5 }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    Required: {item.required_quantity} {item.unit}
                                  </Typography>
                                  {item.is_modified && (
                                    <Chip
                                      label={`Modified from ${item.original_quantity} ${item.unit}`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" color="primary" fontWeight="medium">
                                  {item.total_cost.toFixed(3)} BHD
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {(item.total_cost / item.modified_quantity).toFixed(3)} BHD/{item.unit}
                                </Typography>
                              </Box>
                            </Stack>

                            <Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(availabilityPercentage, 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: alpha(statusColor, 0.1),
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: statusColor,
                                    borderRadius: 4,
                                  },
                                }}
                              />
                              <Stack 
                                direction="row" 
                                justifyContent="space-between" 
                                alignItems="center"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Availability Status
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: statusColor,
                                    fontWeight: 'medium',
                                  }}
                                >
                                  {Math.round(availabilityPercentage)}%
                                </Typography>
                              </Stack>
                            </Box>

                            {item.notes && (
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                  Notes
                                </Typography>
                                <Typography variant="body2">
                                  {item.notes}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" color="primary" fontWeight="medium">
                          Total Processed Items Cost
                        </Typography>
                        <Typography variant="h6" color="primary" fontWeight="medium">
                          {calculateProcessedItemsCost().toFixed(3)} BHD
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          </Box> */}

          {/* Enhanced Raw Materials with Accordion */}
          {/* <Box>
            <Accordion
              expanded={expandedSection === "rawMaterials"}
              onChange={handleAccordionChange("rawMaterials")}
              elevation={0}
              sx={{
                bgcolor: 'transparent',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
                sx={{
                  px: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium" color="primary">
                      Raw Materials
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {modifiedIngredients.length} {modifiedIngredients.length === 1 ? 'item' : 'items'} • {modifiedIngredients.reduce((total, item) => total + item.required_quantity, 0)} total units
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" fontWeight="medium">
                    {calculateRawMaterialsCost().toFixed(3)} BHD
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, mt: 2 }}>
                {modifiedIngredients.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      textAlign: 'center'
                    }}
                  >
                    <Icon 
                      sx={{ 
                        fontSize: 40, 
                        color: alpha(theme.palette.text.secondary, 0.2),
                        mb: 1
                      }}
                    >
                      inventory_2
                    </Icon>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No Raw Materials Added
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add raw materials to include in this recipe
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {modifiedIngredients.map((ingredient) => {
                      const status = getAvailabilityStatus(ingredient);
                      return (
                        <Paper
                          key={ingredient.ingredient_id}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                            borderRadius: 2,
                            border: `1px solid ${alpha(
                              ingredient.is_modified
                                ? theme.palette.warning.main
                                : status.color,
                              0.2
                            )}`,
                          }}
                        >
                          <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="subtitle2" fontWeight="medium">
                                  {ingredient.brand_name}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ mt: 0.5 }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    Required: {ingredient.required_quantity} {ingredient.unit}
                                  </Typography>
                                  {ingredient.is_modified && (
                                    <Chip
                                      label={`Modified from ${ingredient.original_quantity} ${ingredient.unit}`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" color="primary" fontWeight="medium">
                                  {ingredient.selected_batches.reduce((total, batch) => 
                                    total + (batch.quantity * batch.cost_per_unit), 0).toFixed(3)} BHD
                                </Typography>
                              </Box>
                            </Stack>

                            <Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(status.percentage, 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: alpha(status.color, 0.1),
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: status.color,
                                    borderRadius: 4,
                                  },
                                }}
                              />
                              <Stack 
                                direction="row" 
                                justifyContent="space-between" 
                                alignItems="center"
                                sx={{ mt: 0.5 }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Availability Status
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: status.color,
                                    fontWeight: 'medium',
                                  }}
                                >
                                  {Math.round(status.percentage)}%
                                </Typography>
                              </Stack>
                            </Box>

                            {ingredient.selected_batches.length > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  gutterBottom
                                >
                                  Selected Batches
                                </Typography>
                                <Stack spacing={1}>
                                  {ingredient.selected_batches.map((batch) => (
                                    <Paper
                                      key={batch._id}
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                        borderRadius: 1.5,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                      }}
                                    >
                                      <Stack 
                                        direction="row" 
                                        justifyContent="space-between" 
                                        alignItems="center"
                                      >
                                        <Stack spacing={0.5}>
                                          <Typography variant="body2" fontWeight="medium">
                                            #{batch._id.slice(-6)} • {batch.quantity} {batch.unit}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {batch.cost_per_unit?.toFixed(3)} BHD/{batch.unit}
                                          </Typography>
                                        </Stack>
                                        <Typography
                                          variant="subtitle2"
                                          color="primary"
                                          fontWeight="medium"
                                        >
                                          {(batch.quantity * batch.cost_per_unit)?.toFixed(3)} BHD
                                        </Typography>
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" color="primary" fontWeight="medium">
                          Total Raw Materials Cost
                        </Typography>
                        <Typography variant="h6" color="primary" fontWeight="medium">
                          {modifiedIngredients.reduce((total, ingredient) => 
                            total + ingredient.selected_batches.reduce((batchTotal, batch) => 
                              batchTotal + (batch.quantity * batch.cost_per_unit), 0), 0).toFixed(3)} BHD
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box> */}

        {/* Cost Information - Now last */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="600" color="#0891B2">
            Cost Breakdown
          </Typography>
          <Stack spacing={2}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                bgcolor: alpha("#0891B2", 0.02),
                borderRadius: 2,
                border: `1px solid ${alpha("#0891B2", 0.1)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Processed Items
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#0891B2">
                  {processedItemsCost.toFixed(3)} BHD
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {modifiedProcessedItems.length} items • {modifiedProcessedItems.reduce((total, item) => total + item.required_quantity, 0)} units
                </Typography>
                <Icon sx={{ color: alpha("#0891B2", 0.3), fontSize: 24 }}>
                  category
                </Icon>
              </Box>
            </Paper>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                bgcolor: alpha("#0891B2", 0.02),
                borderRadius: 2,
                border: `1px solid ${alpha("#0891B2", 0.1)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Raw Materials
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#0891B2">
                  {rawMaterialsCost.toFixed(3)} BHD
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {modifiedIngredients.length} items • {modifiedIngredients.reduce((total, item) => total + item.required_quantity, 0)} units
                </Typography>
                <Icon sx={{ color: alpha("#0891B2", 0.3), fontSize: 24 }}>
                  inventory_2
                </Icon>
              </Box>
            </Paper>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2.5, 
                bgcolor: alpha("#0891B2", 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha("#0891B2", 0.1)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="subtitle1" color="#0891B2" fontWeight="600" gutterBottom>
                  Total Cost
                </Typography>
                <Typography variant="h4" color="#0891B2" fontWeight="600">
                  {totalCost.toFixed(3)} BHD
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Items: {modifiedProcessedItems.length + modifiedIngredients.length}
                </Typography>
                <Icon sx={{ color: alpha("#0891B2", 0.3), fontSize: 32 }}>
                  payments
                </Icon>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};
